import { useCallback, useMemo, useState } from "react";
import type {
  Node,
  Edge,
  NodeChange,
  NodeDragHandler,
  NodePositionChange,
  XYPosition,
} from "reactflow";
import type {
  SandboxFlowNodeData,
  SandboxLaneNodeData,
  SandboxMachine,
  SandboxNodeState,
} from "@/types/sim-sandbox.types";
import { machineMeasured } from "@/types/sim-sandbox.types";
import { useSandboxDraft } from "../../hooks/useSandboxDraft";
import { activeStages } from "../flow-helpers";
import {
  buildLaneNodes,
  laneRects,
  resolveDropTarget,
  COL_W,
  ROW_H,
  X0,
  LANE_HEADER_H,
} from "../lane-helpers";

/** União dos tipos de node do canvas do sandbox — máquina/âncora e o node de fundo da lane. */
export type SandboxRfNode = Node<SandboxFlowNodeData> | Node<SandboxLaneNodeData>;

// Sincroniza store ↔ React Flow: monta nós (máquinas + lanes de estágio) e edges, e traduz
// eventos (click→select, drag→mover de estágio/criar estágio via hit-test em lane-helpers.ts).
// Posição é sempre derivada do layout por estágio (nunca persistida no store) — mas o React Flow
// SÓ reflete visualmente o arraste em tempo real se os deltas de posição forem re-aplicados via
// onNodesChange (nodes controlados por prop, sem isso, não seguem o cursor durante o gesto). Por
// isso `dragPositions` guarda um override efêmero (não é o store) por node em drag; ele some assim
// que o drag termina, e a posição volta a ser 100% derivada do grid. `baseNodes` fica FORA do
// alcance de `dragPositions` (memo próprio) pra um mousemove durante o drag não recalcular
// rowByStage/edges/anchors — só o node em drag muda de posição a cada frame.
function nodeState(
  m: SandboxMachine,
  bottleneckStageKey: string | null | undefined,
  incompatible: boolean
): SandboxNodeState {
  if (!m.present) return "off";
  if (incompatible) return "incompatible";
  if (bottleneckStageKey && m.stageKey === bottleneckStageKey) return "bottleneck";
  if (m.origin === "acervo") return "added";
  return "normal";
}

function isPositionChange(c: NodeChange): c is NodePositionChange & { position: XYPosition } {
  return c.type === "position" && c.position != null;
}

interface Options {
  bottleneckStageKey?: string | null;
  /** Vazão estimada por estágio, exibida na lane. */
  perStage?: Array<{ stageKey: string; throughputUnH: number }>;
  incompatibleKeys?: Set<string>;
  editable: boolean;
  onAddMachine: (stageKey: string) => void;
}

export function useSandboxFlow({
  bottleneckStageKey,
  perStage,
  incompatibleKeys,
  editable,
  onAddMachine,
}: Options) {
  const machines = useSandboxDraft((s) => s.machines);
  const stages = useSandboxDraft((s) => s.stages);
  const edges = useSandboxDraft((s) => s.edges);
  const start = useSandboxDraft((s) => s.start);
  const end = useSandboxDraft((s) => s.end);
  const selectedKey = useSandboxDraft((s) => s.selectedKey);
  const select = useSandboxDraft((s) => s.select);
  const setStage = useSandboxDraft((s) => s.setStage);
  const addStage = useSandboxDraft((s) => s.addStage);
  // Override efêmero de posição (id → {x,y}) enquanto um node está sendo arrastado — só existe
  // pra dar feedback visual em tempo real; nunca é lido fora daqui, nunca marca o rascunho sujo.
  const [dragPositions, setDragPositions] = useState<Record<string, XYPosition>>({});

  const machineList = useMemo(() => Object.values(machines), [machines]);

  // Memo PRÓPRIO das lanes: depende só de estágio/máquina/editable/callback — nunca de
  // seleção/gargalo/incompatibilidade (que só dizem respeito aos nós MACHINE). Selecionar uma
  // máquina não deve invalidar a identidade dos objetos de lane (evita re-render de todo header).
  // A estimativa entra nas lanes: mexer num slider muda o número no desenho, sem
  // competir com a resposta da simulação. Por isso o memo passa a depender também dela.
  const laneNodes = useMemo(
    () =>
      buildLaneNodes(stages, machineList, editable, onAddMachine, {
        perStage,
        bottleneckStageKey,
      }),
    [stages, machineList, editable, onAddMachine, perStage, bottleneckStageKey]
  );

  const { baseNodes, rfEdges } = useMemo(() => {
    const ordered = [...stages].sort((a, b) => a.order - b.order);
    const xByStage = new Map(ordered.map((s, i) => [s.key, X0 + (i + 1) * COL_W]));
    const rowByStage = new Map<string, number>();

    const nodes: Node<SandboxFlowNodeData>[] = machineList.map((m) => {
      const row = rowByStage.get(m.stageKey) ?? 0;
      rowByStage.set(m.stageKey, row + 1);
      return {
        id: m.key,
        type: "MACHINE",
        position: { x: xByStage.get(m.stageKey) ?? X0 + COL_W, y: LANE_HEADER_H + row * ROW_H },
        draggable: editable,
        selected: selectedKey === m.key,
        data: {
          label: m.name,
          code: m.code,
          present: m.present,
          origin: m.origin,
          stageKey: m.stageKey,
          state: nodeState(m, bottleneckStageKey, incompatibleKeys?.has(m.key) ?? false),
          // Badge "Medido" do nó: qualquer parâmetro medido (não só o escalar legado),
          // p/ concordar com os badges por-parâmetro do sheet (feed-only-measured aparece).
          provenance: machineMeasured(m) ? "measured" : "assumed",
        },
      };
    });

    const anchor = (
      key: string,
      x: number,
      type: "START" | "END",
      label: string
    ): Node<SandboxFlowNodeData> => ({
      id: key,
      type,
      position: { x, y: LANE_HEADER_H },
      draggable: false,
      data: { label, code: "", present: true, origin: "line", stageKey: "", state: "normal" },
    });
    nodes.push(anchor(start.key, X0, "START", "Início"));
    nodes.push(anchor(end.key, X0 + (ordered.length + 1) * COL_W, "END", "Fim"));

    const active = activeStages(stages, machineList);
    const rf: Edge[] = edges.map((e) => ({
      id: `${e.sourceKey}->${e.targetKey}`,
      source: e.sourceKey,
      target: e.targetKey,
      type: "PRODUCTION_FLOW",
    }));
    const firstActive = active[0];
    const lastActive = active.at(-1);
    if (firstActive && lastActive) {
      for (const m of machineList.filter((x) => x.present && x.stageKey === firstActive.key))
        rf.push({ id: `s->${m.key}`, source: start.key, target: m.key, type: "PRODUCTION_FLOW" });
      for (const m of machineList.filter((x) => x.present && x.stageKey === lastActive.key))
        rf.push({ id: `${m.key}->e`, source: m.key, target: end.key, type: "PRODUCTION_FLOW" });
    }
    return { baseNodes: nodes, rfEdges: rf };
  }, [
    machineList,
    stages,
    edges,
    start,
    end,
    selectedKey,
    bottleneckStageKey,
    incompatibleKeys,
    editable,
  ]);

  // Só aplica o override de drag no passo final (barato: um map, não recalcula rowByStage/edges) —
  // fora de drag, `dragPositions` está vazio e nem esse map roda.
  const rfNodes = useMemo(() => {
    const hasOverride = Object.keys(dragPositions).length > 0;
    const nodes = hasOverride
      ? baseNodes.map((n) => (dragPositions[n.id] ? { ...n, position: dragPositions[n.id]! } : n))
      : baseNodes;
    return [...laneNodes, ...nodes];
  }, [laneNodes, baseNodes, dragPositions]);

  // Só existe pro React Flow poder refletir a posição em tempo real durante o arraste (nodes
  // controlados por prop não seguem o cursor sozinhos sem isso) — grava no override efêmero, NUNCA
  // no store. Ignora qualquer outro tipo de mudança (select/remove/dimensions).
  const onNodesChange = useCallback((changes: NodeChange[]) => {
    const positional = changes.filter(isPositionChange);
    if (positional.length === 0) return;
    setDragPositions((prev) => {
      const next = { ...prev };
      for (const c of positional) next[c.id] = c.position;
      return next;
    });
  }, []);

  // Soltar uma máquina: hit-test do X final contra as lanes. Limpa o override efêmero (o node
  // volta a ser 100% derivado do grid) e resolve: mesma lane ⇒ nada; outra lane existente ⇒ move;
  // além da última ⇒ cria estágio novo. Multi-seleção está desligada na superfície (ver
  // SandboxFlowSurface.tsx) — nunca há mais de 1 node em drag por vez, então só tratar `node` (2º
  // parâmetro) é suficiente; o 3º parâmetro (lista de nodes arrastados) sempre teria só este.
  const onNodeDragStop: NodeDragHandler = useCallback(
    (_event, node) => {
      if (node.type !== "MACHINE") return;
      setDragPositions((prev) => {
        if (!(node.id in prev)) return prev;
        const next = { ...prev };
        delete next[node.id];
        return next;
      });
      const s = useSandboxDraft.getState();
      const m = s.machines[node.id];
      if (!m) return;
      const rects = laneRects(s.stages, Object.values(s.machines));
      const resolution = resolveDropTarget(m.stageKey, node.position.x, rects);
      if (resolution.action === "move") setStage(m.key, resolution.stageKey);
      else if (resolution.action === "create") setStage(m.key, addStage());
    },
    [setStage, addStage]
  );

  return {
    rfNodes,
    rfEdges,
    isEmpty: machineList.length === 0,
    onNodeClick: select,
    onPaneClick: useCallback(() => select(null), [select]),
    onNodesChange,
    onNodeDragStop,
  };
}
